import { type ComponentPropsWithoutRef } from 'react'
import clsx from 'clsx'
import { Icon } from '@/common/components/icon'
import * as styles from './Callout.css'

type CalloutColor = 'info' | 'success' | 'caution' | 'error' | 'neutral' | 'insight'

type CalloutProps = {
  /** Title text (optional) */
  title?: string
  /** Description or main content */
  children: React.ReactNode
  /** Color variant */
  color?: CalloutColor
  /** Custom icon name (defaults based on color) */
  icon?: string
  /** Hide the icon */
  hideIcon?: boolean
  /** Show close button */
  dismissible?: boolean
  /** Called when close button is clicked */
  onDismiss?: () => void
  /** Additional CSS class names */
  className?: string
} & Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'title'>

const defaultIcons: Record<CalloutColor, string> = {
  info: 'info',
  success: 'check_circle',
  caution: 'warning',
  error: 'error',
  neutral: 'info',
  insight: 'diamond',
}

/**
 * A callout component for displaying important messages, warnings, or information.
 *
 * @example
 * ```tsx
 * <Callout color="info">
 *   This is an informational message.
 * </Callout>
 *
 * <Callout color="success" title="Success!">
 *   Your changes have been saved.
 * </Callout>
 *
 * <Callout color="error" dismissible onDismiss={() => setVisible(false)}>
 *   An error occurred. Please try again.
 * </Callout>
 * ```
 */
export function Callout({
  title,
  children,
  color = 'info',
  icon,
  hideIcon = false,
  dismissible = false,
  onDismiss,
  className,
  role,
  ...props
}: CalloutProps) {
  const iconName = icon ?? defaultIcons[color]
  const hasTitle = Boolean(title)

  // `alert` is an assertive live region, which is right for the alert colors and
  // wrong for an insight that is on screen at load — it would interrupt a screen
  // reader on arrival. Insights are described by the control they sit under
  // (aria-describedby) instead, so they are read when they are relevant.
  const resolvedRole = role ?? (color === 'insight' ? undefined : 'alert')

  return (
    <div
      role={resolvedRole}
      className={clsx(styles.container({ color }), className)}
      {...props}
    >
      {!hideIcon && (
        <div className={styles.iconWrapper({ color })}>
          <Icon name={iconName} size={20} />
        </div>
      )}

      <div className={styles.content}>
        {title && <p className={styles.title({ color })}>{title}</p>}
        <p className={styles.description({ color, hasTitle })}>{children}</p>
      </div>

      {dismissible && (
        <button
          type="button"
          className={styles.closeButton({ color })}
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <Icon name="close" size={18} />
        </button>
      )}
    </div>
  )
}
